import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Economia", "Ambiente", "Trasporti", "Sanità", "Istruzione", "Diritti", "Sicurezza"];

export default function Committee() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [questions, setQuestions] = useState([]);
  const now = new Date();
  const iso = (d) => new Date(d).toISOString();
  const [form, setForm] = useState({
    title: "", description: "", source_url: "", category: "Economia", country: "",
    committee_id: "", options: "Favorevole\nContrario\nAstenuto",
    opens_at: now.toISOString().slice(0, 16),
    closes_at: new Date(now.getTime() + 7 * 864e5).toISOString().slice(0, 16),
  });

  const loadQuestions = () => api.get("/questions").then(({ data }) => setQuestions(data));
  useEffect(() => {
    if (user === false || (user && user.role === "citizen")) { nav("/"); return; }
    api.get("/countries").then(({ data }) => { setCountries(data); if (data[0]) setForm((f) => ({ ...f, country: data[0].code })); });
    api.get("/committees").then(({ data }) => setCommittees(data));
    loadQuestions();
  }, [user, nav]);

  useEffect(() => {
    const match = committees.filter((c) => c.country === form.country);
    if (match[0]) setForm((f) => ({ ...f, committee_id: match[0].id }));
  }, [form.country, committees]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cName = (c) => (lang === "it" ? c.name_it : c.name_en);

  const submit = async (e) => {
    e.preventDefault();
    const options = form.options.split("\n").map((o) => o.trim()).filter(Boolean);
    if (options.length < 2) { toast.error("min 2 options"); return; }
    try {
      await api.post("/questions", {
        ...form, options, opens_at: iso(form.opens_at), closes_at: iso(form.closes_at),
      });
      toast.success(t("committee.created"));
      loadQuestions();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!user) return <div className="max-w-5xl mx-auto px-4 py-12 text-muted-foreground">{t("common.loading")}</div>;
  const countryCommittees = committees.filter((c) => c.country === form.country);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <h1 className="font-heading text-3xl font-black tracking-tighter flex items-center gap-2"><Users className="h-7 w-7 text-primary" />{t("committee.title")}</h1>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <Card className="p-6">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Plus className="h-5 w-5 text-primary" />{t("committee.new")}</h2>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>{t("committee.form.title")}</Label><Input data-testid="q-title" value={form.title} onChange={set("title")} required className="mt-1.5" /></div>
            <div><Label>{t("committee.form.desc")}</Label><Textarea data-testid="q-desc" value={form.description} onChange={set("description")} required rows={3} className="mt-1.5" /></div>
            <div><Label>{t("committee.form.source")}</Label><Input data-testid="q-source" value={form.source_url} onChange={set("source_url")} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("common.country")}</Label>
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger data-testid="q-country" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{countries.map((c) => <SelectItem key={c.code} value={c.code}>{cName(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("common.category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="q-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("nav.committee")}</Label>
              <Select value={form.committee_id} onValueChange={(v) => setForm({ ...form, committee_id: v })}>
                <SelectTrigger data-testid="q-committee" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{countryCommittees.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("committee.form.options")}</Label><Textarea data-testid="q-options" value={form.options} onChange={set("options")} rows={3} className="mt-1.5 font-mono text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("committee.form.opens")}</Label><Input data-testid="q-opens" type="datetime-local" value={form.opens_at} onChange={set("opens_at")} className="mt-1.5" /></div>
              <div><Label>{t("committee.form.closes")}</Label><Input data-testid="q-closes" type="datetime-local" value={form.closes_at} onChange={set("closes_at")} className="mt-1.5" /></div>
            </div>
            <Button data-testid="q-submit" type="submit" className="w-full">{t("common.create")}</Button>
          </form>
        </Card>

        <div>
          <h2 className="font-heading text-lg font-bold mb-4">{t("committee.myQuestions")}</h2>
          <div className="space-y-4">
            {questions.map((q, i) => <QuestionCard key={q.id} q={q} index={i} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
